import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useEffect,
  useMemo,
} from 'react';
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import './StripeForm.css';
import FigoLottie from '../FigoLottie';
import spinner from '../../../assets/spinner.json';
import { useSavedElement, useTranslations } from '../../../hooks';

import {
  decodeStripeToken,
  getStripeKeys,
  setStripePaymentMethod,
  setStripePaymentMethodError,
  stripePaymentAttempt,
  undoDefaultPaymentMethod,
} from '../../../actions/stripe';
import { doNothing } from '../../../util';
import { StripeEditDetails } from './StripeEditDetails';
import {
  sendAllPetsFSCLead,
  sendFSCCustomerLead,
  sendFSCPaymentLead,
  sendFSCQuoteLead,

} from '../../../actions';
import { ORIGIN_EB } from '../../../constants';

export const SETUP_INTENT_STATUS = {
  failed: 'requires_payment_method',
  processing: 'processing',
  succeeded: 'succeeded',
};

const StripeWrap = forwardRef(({
  email,
  formClass = '',
  hasDefaultsPaymentMethod = false,
  loading = false,
  name,
  noButton = false,
  onConfirm = null,
  onError = null,
  onHideForm = () => { },
  onUndoDefaultPaymentMethod,
  phone,
}, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { t } = useTranslations('quoting');
  const { saveId } = useSavedElement();

  const validateStripe = useCallback(async () => {
    if (!stripe || !elements) {
      return { isValid: false };
    }
    const { error } = await elements.submit();

    if (error) {
      onError?.(error);

      // WIP: how handle stripe error, save error for dev
      dispatch(setStripePaymentMethodError(error.message));
      return { error, isValid: false };
    }
    return { isValid: true };
  }, [dispatch, elements, onError, stripe]);

  const handleSubmitStripe = useCallback(async (event) => {
    if (event) {
      event.preventDefault();
    }
    dispatch(stripePaymentAttempt());

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return { error: "Stripe.js hasn't yet loaded." };
    }

    let result = null;
    saveId('existent-methods-container');

    const { setupIntent, error } = await stripe.confirmSetup({
      // `Elements` instance that was used to
      // create the Payment Element
      confirmParams: {
        // redirect: 'if_required',
        payment_method_data: {
          billing_details: {
            address: { country: 'US' },
            email,
            name,
            phone,
          },
        },
        return_url: 'https://example/billing/payment-method',
      },
      elements,
      redirect: 'if_required',
    }).catch(() => doNothing);

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer
      // (for example, payment details incomplete)
      onError?.(error);
      result = { error };

      // WIP: how handle stripe error, save error for dev
      dispatch(setStripePaymentMethodError(error.message));
    } else {
      const setup = {
        clientSecret: setupIntent?.client_secret,
        id: setupIntent?.id,
        status: setupIntent?.status,
        token: setupIntent?.payment_method,
      };

      await dispatch(setStripePaymentMethod(setup));

      // decode stripe token (payment type, last4 digit, etc.)
      await dispatch(decodeStripeToken(setup.token));

      result = { setup };

      onConfirm?.(setup);
      onHideForm();
    }

    return result;
  }, [
    dispatch,
    elements,
    email,
    name,
    onConfirm,
    onError,
    phone,
    stripe,
    onHideForm,
    saveId,
  ]);

  useImperativeHandle(ref, () => ({
    async handleSubmitStripe() {
      return handleSubmitStripe();
    },
    async validateStripe() {
      return validateStripe();
    },
  }), [handleSubmitStripe, validateStripe]);

  return (
    <form className={formClass} onSubmit={handleSubmitStripe}>
      <PaymentElement options={{
        defaultValues: {
          billingDetails: {
            address: { country: 'US' },
            email,
            name,
          },
        },
        fields: { billingDetails: { address: { country: 'never' } } },
      }}
      />

      {!noButton ? (
        <button
          className="Your-info-Purchase-button"
          onClick={handleSubmitStripe}
          type="button"
        >
          {loading
            ? (
              <FigoLottie
                animationData={spinner}
                height={30}
                width={100}
              />
            ) : t('purchaseButton')}
        </button>
      ) : null}

      {hasDefaultsPaymentMethod && (
        <button
          className="Your-info-Purchase-button-cancel"
          onClick={onUndoDefaultPaymentMethod}
          type="button"
        >
          {t('purchaseButtonCancel')}
        </button>
      )}
    </form>
  );
});

let stripePromise = null;
export const StripeForm = forwardRef(({
  formClass = '',
  Loading = null,
  noButton = false,
  noEdit = false,
  onConfirm = () => { },
  onError = () => { },
  styleUpdate = false,
}, ref) => {
  const store = useSelector(({ quoting }) => (quoting), shallowEqual);
  const { goToSavedElementAndForgot } = useSavedElement();
  const {
    sessionInformation: { nopCommerceUser },
    stripe: {
      defaultsPaymentMethod,
      paymentMethod,
      stripeClientSecret,
      stripeKey,
      stripeKeyLoading,
    },
    newCustomer: { email, name, lastName, phoneNumber, initialEmail },
  } = store;
  const dispatch = useDispatch();

  const [showStripeForm, setShowStripeForm] = useState(false);

  const options = {
    appearance: {
      rules: {
        '.Input': {
          border: '1px solid #040506',
          borderRadius: '20px',
          color: '#43474b',
        },
        '.Label': {
          color: '#040506',
          fontFamily: 'ivyepic',
          fontSize: '14px',
          fontWeight: '700',
          lineHeight: '21px',
        },
        '.Tab': {
          backgroundColor: '#fefcf9',
          border: '2px solid #60666c',
        },
        '.Tab--selected': {
          backgroundColor: '#d5f5f3',
          border: '2px solid #01313d;',
        },
        '.TabIcon--selected': {
          fill: '#01313d',
        },
      },
      variables: {
        borderRadius: '8px',
        colorDanger: '#c12010',
        colorPrimary: '#01313d',
        colorText: '#040506',
        fontFamily: 'ivyepic',
        fontWeight: '700',
        iconColor: '#6d6e78',
      },
    },
    // passing the SetupIntent's client secret
    clientSecret: stripeClientSecret,
    // Fully customizable with appearance API.
  };

  const hasDefaultsPaymentMethod = useMemo(() => {
    const defaultMethod = defaultsPaymentMethod.creditCard?.tokenId
      || defaultsPaymentMethod.bankAccount?.tokenId;

    return Boolean(defaultMethod);
  }, [defaultsPaymentMethod]);

  const handleFscLeadCalls = useCallback(() => {
    if (email !== initialEmail) {
      dispatch(sendAllPetsFSCLead());
      dispatch(sendFSCQuoteLead());
      dispatch(sendFSCCustomerLead());
    }
    dispatch(sendFSCPaymentLead());
  }, [dispatch, email, initialEmail]);

  const onHideForm = useCallback(() => {
    setShowStripeForm(false);
    handleFscLeadCalls();
  }, [handleFscLeadCalls]);

  const onUndoDefaultPaymentMethod = useCallback(() => {
    const { isTwoDefaults } = store.stripe;
    setShowStripeForm(false);

    const isCreditCard = defaultsPaymentMethod.creditCard?.PaymentProfileId;
    const data = defaultsPaymentMethod.creditCard
      || defaultsPaymentMethod.bankAccount;

    dispatch(undoDefaultPaymentMethod({
      isCreditCard,
      isTwoDefaults,
      paymentMethod: data,
    }));
  }, [defaultsPaymentMethod, dispatch, store.stripe]);

  useEffect(() => {
    const recaptchaLoaded = window.grecaptcha;

    if (recaptchaLoaded && nopCommerceUser.DiamonClientdId) {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(process.env.REACT_APP_RECAPTCHA_KEY, { action: 'submit' })
          .then((token) => {
            dispatch(getStripeKeys({
              origin: ORIGIN_EB,
              recaptchaToken: token,
            }));
          });
      });
    }
  }, [dispatch, nopCommerceUser.DiamonClientdId]);

  useEffect(() => {
    if (!showStripeForm) {
      goToSavedElementAndForgot();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStripeForm]);

  if (stripeKeyLoading || !stripeClientSecret) {
    return (
      Loading && (
        <div className="Stripe-loading">
          <Loading />
        </div>
      )
    );
  }

  if (!stripePromise) {
    stripePromise = loadStripe(stripeKey);
  }

  if (paymentMethod.token && !showStripeForm) {
    return (
      <StripeEditDetails
        handleFscLeadCalls={handleFscLeadCalls}
        noEdit={noEdit}
        setShowStripeForm={setShowStripeForm}
        styleUpdate={styleUpdate}
      />
    );
  }

  return (
    <Elements options={options} stripe={stripePromise}>
      <StripeWrap
        ref={ref}
        clientSecret={stripeClientSecret}
        email={email}
        formClass={formClass}
        hasDefaultsPaymentMethod={hasDefaultsPaymentMethod}
        name={`${name} ${lastName}`}
        noButton={noButton}
        onConfirm={onConfirm}
        onError={onError}
        onHideForm={onHideForm}
        onUndoDefaultPaymentMethod={onUndoDefaultPaymentMethod}
        phone={phoneNumber}
      />
    </Elements>
  );
});
